import React, { useEffect, useState, useMemo, SetStateAction, Dispatch, useCallback } from 'react';
import styled from '@emotion/styled';
import Container from '../../blocks/Container';
import ResultList from '../../blocks/ResultList';
import Button from '../../../atoms/buttons/Button';
import { Content, PrimaryContent } from '../../blocks/FlexLayout';
import { getLocalItem, setLocalItem } from '../../../common';
import { HeartIcon } from '../../../atoms/icons/favoriteIcons';
import { isDarkMode } from '../../../utils/helpers';
import OffersTutorialModal from '../../common/modals/OffersTutorialModal';
import OfferList from './OfferList';
import Loading from '../../../atoms/Loading';
import { ORDER_BY, PER_PAGE } from '../../../constants/offers';
import { OFFERS_NEW_PATH, OFFERS_SENT_PATH } from '../../../constants/paths';
import { useOffers } from '../../../contexts/OffersContext';
import { useLocation } from 'react-router-dom';
import OneTapSwipeOfferLists from './oneTapSwipeOffer/components/OneTapSwipeOfferLists';
import { RootState } from '../../../store';
import { useSelector, useDispatch } from 'react-redux';
import { refreshOffers, updateOffer } from '../../../actions/offerActions';
import { refreshProfile, updateShowTutorialOffer } from '../../../actions/profileActions';
import OfferSubHeader from './OfferSubHeader';
import useMediaBreakpoints from '../../../hooks/useMediaBreakpoints';
import { getNoResults } from './common/helpers';
import OfferTopNav from './OfferTopNav';
import breakpoints from '../../../theme/material/breakpoints';
import { loadMoreButtonStyle } from '../../common/CommonStyle';

const calcOfferListHeight = (shouldShowOneTapOfferGridView: boolean, isFetching: boolean, offersLength: number) => {
  if (shouldShowOneTapOfferGridView) {
    if (isFetching) {
      return { height: 'auto' };
    } else if (offersLength === 0) {
      return { height: window.innerHeight - 17, display: 'flex', alignContent: 'flex-start' };
    } else {
      return { height: 'calc(100vh - 190px)', display: 'flex', alignContent: 'flex-start' };
    }
  } else {
    return { height: 'auto' };
  }
};

const ButtonContainer = styled('div')({
  margin: '16px auto',
  maxWidth: '200px',
});

const OffersListWrapper = styled('div')<{ isListView: boolean }>(
  {
    display: 'flex',
    flexFlow: 'row wrap',
    flex: '1 0 100%',
  },
  ({ isListView }) =>
    isListView && {
      marginLeft: 0,
      ['> * + *']: {
        marginLeft: 0,
      },
      [breakpoints.down(639)]: {
        marginLeft: '0vw',
        width: '100%',
        overflow: 'hidden',
      },
    }
);

const OffersList = styled(ResultList)({
  padding: '26px 40px',
  display: 'flex',
  flexFlow: 'wrap',
  borderRadius: '10px !important',
  boxShadow: 'none !important',
  [breakpoints.down('sm')]: {
    padding: '16px 20px',
    borderRadius: '0 0 10px 10px !important',
  },
  [breakpoints.down(639)]: {
    padding: 0,
  },
  justifyContent: 'center',
});

interface OffersV2Props {
  offerType: string;
  isFirstCall: any;
  isRealtimeFetching: boolean;
  setIsRealtimeFetching: Dispatch<SetStateAction<boolean>>;
}

const Offers: React.FC<OffersV2Props> = ({ offerType, isRealtimeFetching, setIsRealtimeFetching }: OffersV2Props) => {
  const location = useLocation();
  const dispatch = useDispatch();

  const meta = useSelector((state: RootState) => state.meta);
  const profile = useSelector((state: RootState) => state.profile);
  const isFetching = useSelector((state: RootState) => state.offers.isFetching);
  const offerList = useSelector((state: RootState) => state.offers[offerType]);

  const { setOffersActionHandler, setVisitedTabHandler } = useOffers();
  const { isMobile } = useMediaBreakpoints();

  const [orderingType, setOrderingType] = useState('recent');
  const [, setLastOfferType] = useState(offerType);
  const { offer_counts } = profile;
  const [viewType, setViewType] = useState(getLocalItem('offers-view') || 'grid');

  const memoisedCurrentPage = useMemo(() => meta[`offer_${offerType}_current_page`], [meta, offerType]);
  const memoisedTotalPage = useMemo(() => meta[`offer_${offerType}_total_pages`], [meta, offerType]);
  const moreExists = useMemo(
    () => Boolean(memoisedCurrentPage < memoisedTotalPage) && offerList?.length > 0,
    [memoisedCurrentPage, memoisedTotalPage, offerList, offerType]
  );

  const showOfferTutorial = useMemo(
    () => profile.account_config?.show_offer_tutorial,
    [profile.account_config?.show_offer_tutorial]
  );
  const activeRoute = useMemo(() => (location.pathname === OFFERS_NEW_PATH ? 'new' : 'sent'), [location.pathname]);
  const isOneTapModeVariation = isMobile;
  const shouldShowOneTapOfferGridView = useMemo(
    () => activeRoute === 'new' && viewType === 'grid' && isOneTapModeVariation,
    [activeRoute, viewType, isOneTapModeVariation]
  );

  const isGenerous = useMemo(
    () => profile?.profile?.data.account_type === 'Generous',
    [profile?.profile?.data.account_type]
  );

  const subRoutes = useMemo(
    () => [
      {
        page: `Received ${isGenerous ? 'Requests' : 'Offers'} (${offer_counts?.new ?? 0})`,
        headline: `Received ${isGenerous ? 'Requests' : 'Offers'}`,
        icon: {
          default: <HeartIcon width={isMobile ? 11 : 20} height={isMobile ? 11 : 18} isActive={false} />,
          active: <HeartIcon width={isMobile ? 11 : 20} height={isMobile ? 11 : 18} isActive={true} />,
        },
        url: OFFERS_NEW_PATH,
        active: /.*new.*/,
        noResultsTitle: `Your dates are out there!`,
        noResultsMsg: `You don’t  have any offers now, but don’t worry, the more you interact with other members, the more you get!.`,
        noResultsImg: 'offer',
      },
      {
        page: `Sent ${isGenerous ? 'Offers' : 'Requests'} (${offer_counts?.pending ?? 0})`,
        headline: `Sent ${isGenerous ? 'Offers' : 'Requests'}`,
        icon: {
          default: <HeartIcon width={isMobile ? 11 : 20} height={isMobile ? 11 : 18} isActive={false} />,
          active: <HeartIcon width={isMobile ? 11 : 20} height={isMobile ? 11 : 18} isActive={true} />,
        },
        url: OFFERS_SENT_PATH,
        active: /.*sent$/,
        noResultsTitle: 'It takes two to tango!',
        noResultsMsg: 'The more offers you make, the more dates you get (it’s science).',
        noResultsImg: 'offer',
      },
    ],
    [isGenerous, offer_counts, isMobile]
  );

  const handleUpdateOffer = formData => {
    return dispatch(updateOffer(formData));
  };

  const noResults = getNoResults(subRoutes, location.pathname);

  const handleRefreshOffer = (
    route,
    orderType = ORDER_BY,
    firstCall = true,
    perPage = PER_PAGE,
    isRealtime = false,
    forceRefresh = false
  ) => {
    dispatch(refreshProfile());
    dispatch(refreshOffers(route, orderType, firstCall, perPage, isRealtime, forceRefresh));
  };

  const handleLoadMore = (route, orderType = ORDER_BY, firstCall = false, perPage) => {
    dispatch(refreshOffers(route, orderType, firstCall, perPage));
  };

  useEffect(() => {
    setOffersActionHandler({ handleUpdateOffer });
  }, [handleUpdateOffer, setOffersActionHandler]);

  useEffect(() => {
    setVisitedTabHandler(offerType, true);
  }, [offerType, setVisitedTabHandler]);

  const getActivePageTitle = () => {
    const activePage = location.pathname;

    const activeRoute = subRoutes.find(function (routeItem) {
      const regex = routeItem.active;
      const active = regex.test(activePage);

      return active ? routeItem : false;
    });

    return activeRoute && activeRoute.headline;
  };

  useEffect(() => {
    setOrderingType('recent');
    setLastOfferType(offerType);
  }, [offerType]);

  const changeOrder = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setOrderingType(e.target.value);
    setVisitedTabHandler(offerType, true);
    handleRefreshOffer(offerType, e.target.value, true, PER_PAGE, false, true);
  };

  const handleViewSwitcher = useCallback(() => {
    const viewTypeValue = viewType === 'list' ? 'grid' : 'list';

    setViewType(viewTypeValue);
    setLocalItem('offers-view', viewTypeValue);
  }, [viewType]);

  const renderMoreExists = () => {
    if (!moreExists) return;

    return (
      <>
        {isFetching && offerList?.length > 0 ? (
          <ButtonContainer>
            <Loading />
          </ButtonContainer>
        ) : (
          <ButtonContainer>
            <Button
              buttonType="loadMore"
              fit
              onClick={() => {
                handleLoadMore(activeRoute, orderingType, false, PER_PAGE);
              }}
              // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
              customStyle={loadMoreButtonStyle}
            >
              Load More...
            </Button>
          </ButtonContainer>
        )}
      </>
    );
  };

  if (!offer_counts) return null;

  return (
    <Container fullWidth screenTop>
      <Content className="offers-content">
        <OfferTopNav
          isOneTapModeVariation={isOneTapModeVariation}
          subRoutes={subRoutes}
          handleViewSwitcher={handleViewSwitcher}
          viewType={viewType}
          themeDarkMode={isDarkMode()}
        />
        <PrimaryContent>
          <OffersList
            className="offerListContent"
            style={calcOfferListHeight(shouldShowOneTapOfferGridView, isFetching, offerList?.length)}
          >
            <OfferSubHeader
              viewType={viewType}
              isOneTapModeVariation={isOneTapModeVariation}
              shouldShowOneTapOfferGridView={shouldShowOneTapOfferGridView}
              showOfferTutorial={showOfferTutorial}
              handleViewSwitcher={handleViewSwitcher}
              pageTitle={getActivePageTitle() ?? ''}
              changeOrder={changeOrder}
              orderingType={orderingType}
            />

            {shouldShowOneTapOfferGridView ? (
              <OneTapSwipeOfferLists
                pageTitle={getActivePageTitle()}
                isFetching={isFetching}
                offers={offerList}
                moreExists={moreExists}
                isRealTimeEvent={meta?.offer_new_is_realtime_event ?? false}
                loadMore={() => handleLoadMore(activeRoute, 'recent', false, PER_PAGE)}
                isRealtimeFetching={isRealtimeFetching}
                setIsRealtimeFetching={setIsRealtimeFetching}
              />
            ) : (
              <OffersListWrapper data-test-id="offer-list-wrapper" isListView={viewType === 'list'}>
                <OfferList
                  activeRoute={activeRoute}
                  noResultsTitle={noResults.title}
                  noResultsMsg={noResults.msg}
                  noResultsImg={noResults.img}
                  viewType={viewType}
                  pageTitle={getActivePageTitle()}
                  offerType={offerType}
                />
              </OffersListWrapper>
            )}

            {!shouldShowOneTapOfferGridView && renderMoreExists()}
          </OffersList>
        </PrimaryContent>
      </Content>
      <OffersTutorialModal
        showTutorial={showOfferTutorial}
        isFirstTimeOffer
        onClose={() => showOfferTutorial && dispatch(updateShowTutorialOffer(false))}
        linkStyle={{ display: 'none' }}
      />
    </Container>
  );
};

export default Offers;
